import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { PreBillingArticle, PreBillingBook } from '@classes/pre-billing/preBilling';
import { PreBillingGeneratePdfRequest } from '@classes/pre-billing/preBillingGeneratePdfRequest';
import { Site } from '@classes/site';
import { DatatableBaseComponent } from '@components/datatable-base.component';
import { PreBillingService } from '@services/preBilling.service';
import { UiService } from '@services/ui.service';
import { validateHeaderName } from 'http';
import moment from 'moment';

@Component({
  selector: 'app-pre-billing-new',
  templateUrl: './pre-billing-new.component.html',
  styleUrls: ['./pre-billing-new.component.scss']
})
export class PreBillingNewComponent extends DatatableBaseComponent implements OnInit, AfterViewInit {

  @Input() site: Site;

  private readonly LOCAL_STORAGE_DISPLAYED_COLUMN_IDENTIFIER = "PreBillingDisplayedColumnsMenu";
  private readonly DEFAULT_COL_CHECKBOX_VALUE = true;
  public readonly PAGINATION_PAGE_SIZE = 10;

  preBillingBook: PreBillingBook;

  dataSource: MatTableDataSource<PreBillingArticle> = new MatTableDataSource<PreBillingArticle>();
  @ViewChild(MatPaginator) paginator: MatPaginator;

  dateFilter = this.fb.group({
    range: new UntypedFormGroup({
      fromStartDate: new UntypedFormControl(null, Validators.required),
      toStartDate: new UntypedFormControl(null, Validators.required),
    }),
  });

  selectedColForm = this.fb.group({
    code: [this.DEFAULT_COL_CHECKBOX_VALUE],
    quantity: [this.DEFAULT_COL_CHECKBOX_VALUE],
    senderStep: [this.DEFAULT_COL_CHECKBOX_VALUE],
    urgency: [this.DEFAULT_COL_CHECKBOX_VALUE],
    lot: [this.DEFAULT_COL_CHECKBOX_VALUE],
    moment: [this.DEFAULT_COL_CHECKBOX_VALUE],
  });

  displayedColumns: string[] = [];
  displayedColumnsMenu: any[] = [
    { editable: true, name: 'code', selected: true, visible: false },
    { editable: true, name: 'quantity', selected: true, visible: false },
    { editable: true, name: 'senderStep', selected: true, visible: false },
    { editable: true, name: 'urgency', selected: true, visible: false },
    { editable: true, name: 'lot', selected: true, visible: false },
    { editable: true, name: 'totalWaitingPrice', selected: true, visible: false },
    { editable: true, name: 'unitPrice', selected: true, visible: false },
    { editable: true, name: 'moment', selected: true, visible: false },
    { editable: true, name: 'totalPrice', selected: true, visible: false },
  ];

  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private _preBillingSrv: PreBillingService
  ) {
    super(uiSrv);
  }

  ngOnInit(): void {
    this._checkIfColumnsInStorage();
    this._initSelectedColFormSubscriptions();
  }

  ngAfterViewInit(): void {
    this._initPaginator();
  }

  private _initPaginator(): void {
    if (!this.dataSource || !this.paginator) return;

    this.dataSource.paginator = this.paginator;
  }

  private _initSelectedColFormSubscriptions(): void {
    this.addSubscription(
      this.selectedColForm.get("senderStep").valueChanges.subscribe(value => {
        if (value)
          this.selectedColForm.get("moment").setValue(true);
      })
    );

    this.addSubscription(
      this.selectedColForm.get("moment").valueChanges.subscribe(value => {
        if (!value)
          this.selectedColForm.get("senderStep").setValue(false);
      })
    );
  }

  private _checkIfColumnsInStorage(): void {
    if (localStorage.getItem(this.LOCAL_STORAGE_DISPLAYED_COLUMN_IDENTIFIER)) {
      this.displayedColumnsMenu = JSON.parse(
        localStorage.getItem(this.LOCAL_STORAGE_DISPLAYED_COLUMN_IDENTIFIER)
      );
    }
  }

  public onFilterSubmited(): void {
    if (this.dateFilter.valid)
      this._loadData();
  }

  private _loadData(): void {
    this.isLoading = true;
    const startDate: Date = this.dateFilter?.get("range")?.get("fromStartDate")?.value;
    const endDate: Date = this.dateFilter?.get("range")?.get("toStartDate")?.value;

    if (!this.site || !startDate || !endDate) return;

    this._preBillingSrv.getNew(this.site.id, startDate, endDate).subscribe(result => {
      this.preBillingBook = result;
      this._initTable();
      this.isLoading = false;
    });
  }

  public onResetFilters(): void {
    this.dateFilter.reset();
    this.preBillingBook = null;
  }

  public drop(event: CdkDragDrop<string[]>): void {
    // Swap the elements around
    moveItemInArray(
      this.displayedColumnsMenu,
      event.previousIndex,
      event.currentIndex
    );
    this._saveDisplayedColumnsToLocalStorage();
    this.setDisplayedColumns();
  }

  public onSelectMenu(event, name): void {
    this.displayedColumnsMenu.find((f) => f.name === name).selected =
      event.checked;
    this._saveDisplayedColumnsToLocalStorage();
    this.setDisplayedColumns();
  }

  private _saveDisplayedColumnsToLocalStorage(): void {
    localStorage.setItem(
      this.LOCAL_STORAGE_DISPLAYED_COLUMN_IDENTIFIER,
      JSON.stringify(this.displayedColumnsMenu)
    );
  }

  private setDisplayedColumns(): void {
    this.displayedColumns = this.displayedColumnsMenu
      .filter((f) => (f || {}).selected)
      .map((m) => m.name);
  }

  private _initTable(): void {
    this.dataSource.data = this.preBillingBook.preBillingArticles;
    this.setDisplayedColumns();
  }

  public onPdfExportClicked(): void {
    var request = this.generatePreBillingGeneratePdfRequest();
    this._preBillingSrv.generatePdf(request).subscribe();
  }

  private generatePreBillingGeneratePdfRequest(): PreBillingGeneratePdfRequest {
    var result = new PreBillingGeneratePdfRequest();
    result.articles = this.preBillingBook.preBillingArticles;
    result.startDate = this.getFormatedDate("fromStartDate");
    result.endDate = this.getFormatedDate("toStartDate");
    result.siteId = this.site.id;
    result.displayCode = this.selectedColForm.get("code").value;
    result.displayPriceType = this.selectedColForm.get("moment").value;
    result.displayQuantity = this.selectedColForm.get("quantity").value;
    result.displaySteps = this.selectedColForm.get("senderStep").value;
    result.displayUrgency = this.selectedColForm.get("urgency").value;
    result.displayLot = this.selectedColForm.get("lot").value;


    return result;
  }

  private getFormatedDate(identifier: string): string {
    return moment(new Date(this.dateFilter.get("range").get(identifier).value)).startOf('day').utc().format("YYYY-MM-DD HH:mm");
  }

  public hasWarning(): boolean {
    if (!this.preBillingBook || !this.preBillingBook.incompleteBillingDatas) return false;

    return this.preBillingBook.incompleteBillingDatas.length > 0;
  }

  public hasPreBillingArticles(): boolean {
    if (!this.preBillingBook || !this.preBillingBook.preBillingArticles) return false;

    return this.preBillingBook.preBillingArticles.length > 0;
  }

  public hasNoBillingArticle(): boolean {
    if (!this.preBillingBook || !this.preBillingBook.preBillingArticles) return false;

    return this.preBillingBook.preBillingArticles.length === 0;
  }
}
